/**
 * @fileoverview Organisation Detail Page
 *
 * Purpose: Detailed admin view of a single organisation.
 * URL: /admin/organisations/$orgId
 * Protection: Requires super_admin role
 *
 * Sections:
 * 1. Overview card (name, WorkOS ID, created date, member count, job count)
 * 2. Members table (user + role + joined date)
 *
 * Dependencies:
 * - Convex: admin.organisations.getById
 * - shadcn Card, Badge, Table
 */

import { createFileRoute, redirect, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Card, CardContent, CardHeader, CardTitle } from "@/ui/primitives/card";
import { Badge } from "@/ui/primitives/badge";
import { AiUsageCard } from "@/components/agents/universal";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/composed/data-display/table";
import { UserAvatar } from "@/ui/composed/user-avatar";
import { ArrowLeft, Building2 } from "lucide-react";

/** Shape of a member in the org detail response */
interface OrgMember {
  userId: Id<"users">;
  email: string;
  firstName?: string;
  lastName?: string;
  role: string;
  joinedAt: number;
  avatarUrl?: string;
}

export const Route = createFileRoute(
  "/_app/_auth/admin/_layout/organisations/$orgId",
)({
  beforeLoad: async ({ context }) => {
    const user = await context.queryClient.ensureQueryData(
      convexQuery(api.users.queries.getCurrentUser, {}),
    );
    if (!user?.isOwner) {
      throw redirect({ to: "/admin/master-library" });
    }
  },
  component: OrgDetailPage,
});

function OrgDetailPage() {
  const { orgId } = Route.useParams();

  const { data: org } = useQuery(
    convexQuery(api.admin.organisations.getById, {
      orgId: orgId as Id<"organizations">,
    }),
  );

  if (org === undefined) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-primary" />
      </div>
    );
  }

  if (org === null) {
    return (
      <div className="flex h-64 items-center justify-center">
        <p className="text-muted-foreground">Organisation not found.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Back link */}
      <Link
        to="/admin/organisations"
        className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Organisations
      </Link>

      {/* AI usage — this org's spend this month */}
      <AiUsageCard orgId={orgId as Id<"organizations">} />

      {/* Overview Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            {org.name}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 text-sm md:grid-cols-2">
            <div>
              <span className="text-muted-foreground">WorkOS ID:</span>{" "}
              <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
                {org.workOsOrgId}
              </code>
            </div>
            {org.slug && (
              <div>
                <span className="text-muted-foreground">Slug:</span> {org.slug}
              </div>
            )}
            <div>
              <span className="text-muted-foreground">Created:</span>{" "}
              {new Date(org._creationTime).toLocaleDateString()}
            </div>
            <div>
              <span className="text-muted-foreground">Members:</span>{" "}
              {org.members.length}
            </div>
            <div>
              <span className="text-muted-foreground">Jobs:</span>{" "}
              {org.jobCount}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Members Table */}
      <Card>
        <CardHeader>
          <CardTitle>Members ({org.members.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {org.members.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Joined</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {org.members.map((member: OrgMember) => (
                  <TableRow key={member.userId}>
                    <TableCell>
                      <Link
                        to="/admin/users/$userId"
                        params={{ userId: member.userId }}
                        className="flex items-center gap-3 hover:underline"
                      >
                        <UserAvatar
                          avatarUrl={member.avatarUrl}
                          firstName={member.firstName}
                          lastName={member.lastName}
                          email={member.email}
                          className="h-8 w-8"
                        />
                        <div className="flex flex-col">
                          <span className="text-sm font-medium">
                            {[member.firstName, member.lastName]
                              .filter(Boolean)
                              .join(" ") || member.email}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {member.email}
                          </span>
                        </div>
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{member.role}</Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {new Date(member.joinedAt).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="py-4 text-center text-sm text-muted-foreground">
              No members.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
